import {GradeMapPipe} from '@app/core/pipes/friendly-names-pipes/grade-map.pipe';
import {Guid} from '@app/views/databrowsing/model/common.data.models';
import {RoleType, UserRole} from '@app/views/databrowsing/model/rostering.models';
import {copyData} from '@app/core/model/integration-models';

export class DuplicatesMetadata {
    constructor(
        public duplicatesMetadataModelList: DuplicatesMetadataModel[],
        public duplicatesPersonStatusMetadataModels: DuplicatesPersonStatusMetadataModels[]
    ) { }
}

export class DuplicatesMetadataModel {
    constructor(
        public sourceDname: string,
        public sourceFriendlyName: string,
        public sourceType: string,
        public rosterType: string,
        public quantity: number
    ) { }
}

export class DuplicatesPersonStatusMetadataModels {
    constructor(
        public personStatus: string,
        public quantity: number
    ) { }
}

export class DuplicateModel {
    constructor(
        public actions: string[],
        public appName: string,
        public appIcon: string,
        public duplicateIssueId: string,
        public schools: Map<string, string> = new Map(),
        public duplicates: Array<SourceEntityModel> = [],
        public duplicatesQuantity: number,
        public identifier: string,
        public organizationName: string,
        public rosterType: string,
        public sourceDname: string,
        public sourceEntities: Array<SourceEntityModel>,
        public sourceFriendlyName: string,
        public sourceType: string,
        public title: string,
        public violatedDomainsOfUniqueness: ShortDomainOfUniquenessModel
    ) { }
}

export class DuplicatesFilterItem {
    constructor(
        public name: string,
        public value: string,
        public quantity: number,
        public selected: boolean
    ) {
    }
}

export class DuplicatesSearchConfig {
    constructor(
        public rosterType: string,
        public dataSource: string,
        public sessionRef: number,
        public subOrgGuid: string,
        public lastIdentifier: string,
        public rowCount: number,
        public direction: string,
        public status: string,
        public searchChunk: string,
        public showStage: boolean = false,
        public organizationGuid?: string
    ) { }
}

export class DuplicatesMetadataConfig {
    constructor(
        public dataSource: string,
        public rosterType: string,
        public sessionRef: string,
        public searchChunk: string,
        public subOrgGuid: string,
        public status: string,
        public showStage: boolean = false,
        public organizationGuid?: string
    ) { }
}

export class SourceEntityModel {
    public properties: SourceEntityPropertyModel[];
    public linkedGuidsMetadata: LinkedGUIDsMetadataMap;
    public issueEntitiesSysIds?: Map<string,  {[key: string]: string}>;
}

export class SourceEntityPropertyModel {
    public friendlyName: string;
    public name: string;
    public order: number;
    public value: string;
    public values: Array<any>;

    constructor(
        friendlyName?: string, name?: string, order?: number, value?: string, values?: Array<any>
    ) {
        this.friendlyName = friendlyName;
        this.name = name;
        this.order = order;
        this.value = value;
        this.values = values;
    }
}

export interface LinkedGUIDsMetadataMap {
    [key: string]: LinkedGUIDMetadata;
}

export interface LinkedGUIDMetadata {
    friendlyName: string;
    [key: string]: string;
}

export class DuplicateColumn {
    public rows: SourceEntityPropertyModel[];
    public linkedGUIDsMetadata: LinkedGUIDsMetadataMap;
    public guid: string;
    public entityIndex: number;
    public sisIdsUniqueQuantity: number;
    public isGG4LEntity: boolean;
    public isIgnoreAllowed: boolean;
    public primaryRole: string;
    public isSetAsPrimaryAllowed: boolean;

    static compareDuplicateEntity(a: DuplicateColumn, b: DuplicateColumn, primary: DuplicateColumn = null): number {
        if ((a.isGG4LEntity && !a.isIgnoreAllowed) && !(b.isGG4LEntity && !b.isIgnoreAllowed)) {
            return -1;
        }
        if ((b.isGG4LEntity && !b.isIgnoreAllowed) && !(a.isGG4LEntity && !a.isIgnoreAllowed)) {
            return 1;
        }
        if (primary && (a.guid === primary.guid) && (b.guid !== primary.guid)) {
            return -1;
        }
        if (primary && (b.guid === primary.guid) && (a.guid !== primary.guid)) {
            return 1;
        }
        const roleOrder = (USER_TYPE_PRIORITY_ORDER[a.primaryRole] || 5) - (USER_TYPE_PRIORITY_ORDER[b.primaryRole] || 5);
        return (roleOrder === 0) ? (a.entityIndex - b.entityIndex) : roleOrder;
    }

    constructor(entity: SourceEntityModel, guid?: string, entityIndex?: number, isGG4LEntity?: boolean, isIgnoreAllowed?: boolean) {
        this.rows = entity.properties;
        this.linkedGUIDsMetadata = entity.linkedGuidsMetadata;
        this.primaryRole = this.getPrimaryRole(entity.properties);
        this.isGG4LEntity = isGG4LEntity;
        this.guid = guid;
        this.entityIndex = entityIndex;
        this.isIgnoreAllowed = isIgnoreAllowed;
        this.setSisIdsUniqueQuantity();
    }

    setAsGG4LEntity() {
        this.isGG4LEntity = true;
    }

    updateRowValue(propertyName: string, value: any) {
        const attr = this.rows ? this.rows.find((property: SourceEntityPropertyModel) => property.name == propertyName) : null;

        if (attr) {
            attr.value = value;
        }
    }

    getSisIds() {
        const sisIdsMap = {};

        this.rows.forEach((prop: SourceEntityPropertyModel) => {
            if (this.isSisIdProperty(prop.name)) {
                sisIdsMap[this.getSisIdName(prop.name)] = prop.value;
            }
        });

        return sisIdsMap;
    }

    getSisIdName(name: string = ''): string {
        return name == 'localId' ? name : name.substring(7, (name.length - 1));
    }

    setSisIdsUniqueQuantity(value?: number) {
        if (typeof  value == 'undefined') {
            this.sisIdsUniqueQuantity = OperationSourceEntityPropertyModel.uniqueValues(Array.from(Object.values(this.getSisIds())).filter(val => !!val)).length || 0;
        } else {
            this.sisIdsUniqueQuantity = value;
        }
    }

    isSisIdProperty(name): boolean {
        const sisIdRegExp = new RegExp(`^(sisId\[[a-zA-z0-9]+\]|localId)$`, 'g');

        return name && !!sisIdRegExp.test(name);
    }

    getPrimaryRole(rows: SourceEntityPropertyModel[]): string {
        // use 'userRoles.isPrimaryOrgForPersonType
        const userRolesProperty = (rows || []).find((property: SourceEntityPropertyModel) => property.name === USER_ROLES_PROPERTY_NAME);
        if (!userRolesProperty || !userRolesProperty.values || (userRolesProperty.values.length === 0)) {
            return null;
        }

        const userRoles = <UserRole[]>userRolesProperty.values;
        const primaryRole = userRoles.find((userRole: UserRole) => userRole.isPrimaryOrgForPersonType);
        if (primaryRole) {
            return primaryRole.personType;
        }

        const userRolesCopy = copyData(userRoles);
        // ToDo: Test!!!
        // Sort by User Type priority order - STUDENT, TEACHER, ADMIN, CONTACT
        userRolesCopy.sort((a: UserRole, b: UserRole) => {
            if (a.orgGuid && b.orgGuid && (a.orgGuid.guidValue === b.orgGuid.guidValue)) {
                if (a.roleTypeForThisOrg == RoleType.PRIMARY.toUpperCase()) {
                    return 1;
                }
                if (b.roleTypeForThisOrg == RoleType.PRIMARY.toUpperCase()) {
                    return -1;
                }
            }
            return (USER_TYPE_PRIORITY_ORDER[a.personType] || 5) - (USER_TYPE_PRIORITY_ORDER[b.personType] || 5);
        });
        return userRolesCopy[0].personType;
    }
}

export class OperationSourceEntityPropertyModel extends SourceEntityPropertyModel {
    public duplicateValue: string;
    public duplicateValues: Array<any> = [];
    public newValue: string ;
    public newValues: Array<any> = [];
    public linkedEntityGUIDsMetadataMap: LinkedGUIDsMetadataMap = {};
    public newLinkedEntityGUIDsMetadataMap: LinkedGUIDsMetadataMap = {};
    public duplicateLinkedEntityGUIDsMetadataMap: LinkedGUIDsMetadataMap = {};
    public attrHasConflict = false;
    public attrHasDifferentValues = false;
    public attrConflictResolved = false;
    public sisIdAttrValue = false;
    public allowedToMergeSPEntity = true;
    public lastChangesId = null;

    readonly PHOTO_PROPERTY_NAME = 'profilePhotoUrl';
    readonly HTML_PROPERTIES_LIST = [/*this.PHOTO_PROPERTY_NAME*/];
    readonly RELATIONSHIP_PROPERTIES_LIST = ['relationships'];
    readonly PHONE_NUMBER_PROPERTIES_LIST = ['phoneNumber.number', 'otherPhoneNumbers'];
    readonly PERSONS_ARRAY_PROPERTIES_LIST = ['otherStaffs', 'students'];
    // readonly NOT_CONCAT_PROPERTIES_LIST = ['demographics.nationalities', 'demographics.languages', 'otherNames'];
    readonly HIDDEN_PROPERTIES_LIST = ['guid', 'createdAt', 'lastUpdated', 'sisIds', 'userType', 'primaryOrg'];
    readonly HIDDEN_PROPERTIES_WILDCARD_LIST = ['userRoles.'];
    readonly READONLY_PROPERTIES_LIST = ['effectiveTimestamp'];
    readonly ENROLLMENT_PROPERTIES = ['studentEnrollment.schoolId', 'primaryAssignment.schoolId', 'schoolId'];
    readonly OTHER_ENROLLMENT_PROPERTIES_LIST = ['otherEnrollments', 'otherAssignments', 'otherSchoolIds'];
    readonly NOT_ALLOWED_TO_MERGE_SP_ENTITY = ['email.emailAddress', 'credentials.username', 'credentials.password', 'phoneNumber.number'];
    readonly MERGE_OBJECTS_LIST_PROPERTIES_LIST = [USER_ROLES_PROPERTY_NAME];

    constructor(friendlyName?: string, name?: string, order?: number, value?: string, values?: Array<any>,
        duplicateValue?: string, duplicateValues?: Array<any>, newValue?: string, newValues?: Array<any>
    ) {
        super(friendlyName, name, order, value, values);
        this.duplicateValue = duplicateValue || '';
        this.duplicateValues = duplicateValues ? duplicateValues : [];
        this.newValue = newValue || '';
        this.newValues = newValues ? newValues : [];
        this.attrHasConflict = this.isConflictAttr();
        this.attrHasDifferentValues = this.isDifferentAttrValues();
        this.attrConflictResolved = this.isResolvedConflictAttr();
    }

    setPrimaryValue(sourceEntity: SourceEntityPropertyModel) {
        this.name = sourceEntity.name;
        this.friendlyName = sourceEntity.friendlyName;
        this.order = sourceEntity.order;
        this.value = sourceEntity.value;
        this.values = Object.assign([], sourceEntity.values);
        this.sisIdAttrValue = this.isSisIdAttr();
        this.allowedToMergeSPEntity = this.isAllowedToMergeSPEntity();
        if (this.duplicateValue || (this.duplicateValues && this.duplicateValues.length > 0)) {
            this.updateAttrStatus();
        } else {
            this.setDefaultAttrStatus();
        }
    }

    setDuplicateValue(sourceEntity: SourceEntityPropertyModel) {
        this.duplicateValue = sourceEntity.value;
        this.duplicateValues = Object.assign([], sourceEntity.values);
        this.updateAttrStatus();
    }

    setNewValue(optionalValues?: any, skipValue?: any) {
        if (optionalValues && !this.attrConflictResolved) {
            const resultArray = this.values.concat(optionalValues || []).filter(val => JSON.stringify(val) !== JSON.stringify(skipValue));

            this.newValue = this.value;
            this.newValues = Object.assign([], OperationSourceEntityPropertyModel.uniqueValues(resultArray));
        } else {
            const resultArray = this.duplicateValues
                .concat(this.isOtherEnrollmentProperty() ? this.values : [], optionalValues || [])
                .filter(val => JSON.stringify(val) !== JSON.stringify(skipValue));

            this.newValue = this.duplicateValue;
            this.newValues = Object.assign([], OperationSourceEntityPropertyModel.uniqueValues(resultArray));
        }
        this.updateAttrStatus();
    }

    mergeValues(optionalValues?: any, skipValue?: any) {
        /*if (optionalValues && !this.attrConflictResolved) {
            const resultArray = this.values.concat(optionalValues || []).filter(val => JSON.stringify(val) !== JSON.stringify(skipValue));

            this.newValue = this.value;
            this.newValues = Object.assign([], OperationSourceEntityPropertyModel.uniqueValues(resultArray));
        } else {
            const resultArray = this.duplicateValues
                .concat(this.values, optionalValues || [])
                .filter(val => JSON.stringify(val) !== JSON.stringify(skipValue));

            this.newValue = this.duplicateValue;
            this.newValues = Object.assign([], OperationSourceEntityPropertyModel.uniqueValues(resultArray));
        }*/
        if (this.name === USER_ROLES_PROPERTY_NAME) {
            const values = ((this.newValues || []).length === 0) ? this.values : this.newValues;
            this.newValues = UserRole.mergeList(values, this.duplicateValues);
        }
        this.updateAttrStatus();
    }

    replaceSisIdValue(changesId: string = null) {
        const oldPrimaryValue = this.attrConflictResolved ? this.newValue : this.value;
        this.newValue = this.duplicateValue;
        this.duplicateValue = oldPrimaryValue;

        this.attrConflictResolved = !this.attrConflictResolved;
        this.attrHasConflict = (this.value !== this.duplicateValue) && (this.order > 0);
        this.lastChangesId = changesId;
    }

    resetNewValue() {
        this.newValue = '';
        this.newValues = [];
        this.newLinkedEntityGUIDsMetadataMap = {};
        this.updateAttrStatus();
    }

    updatePrimaryAndResetNewValue(sourceEntity: SourceEntityPropertyModel) {
        this.setPrimaryValue(sourceEntity);
        this.resetNewValue();
    }

    resetDuplicateValue() {
        this.duplicateValue = '';
        this.duplicateValues = [];
        this.duplicateLinkedEntityGUIDsMetadataMap = {};
        this.updateAttrStatus();
    }

    updatePrimaryAndResetDuplicateValue(sourceEntity: SourceEntityPropertyModel) {
        this.setPrimaryValue(sourceEntity);
        this.resetDuplicateValue();
    }

    setPrimaryLinkedGUIDsMetadataMap(metadataMap: LinkedGUIDsMetadataMap) {
        if (this.value) {
            this.linkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.value, metadataMap);
        } else if (this.values && this.values.length > 0) {
            this.linkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.values, metadataMap);
        }
    }

    setNewLinkedGUIDsMetadataMap(optionalMap?: LinkedGUIDsMetadataMap) {
        const metadataMap = {...this.linkedEntityGUIDsMetadataMap, ...this.duplicateLinkedEntityGUIDsMetadataMap, ...optionalMap};
        if (this.newValue) {
            this.newLinkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.newValue, metadataMap);
        } else if (this.newValues && this.newValues.length > 0) {
            this.newLinkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.newValues, metadataMap);
        }
    }

    setDuplicateLinkedGUIDsMetadataMap(metadataMap: LinkedGUIDsMetadataMap) {
        if (this.duplicateValue) {
            this.duplicateLinkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.duplicateValue, metadataMap);
        } else if (this.duplicateValues && this.duplicateValues.length > 0) {
            this.duplicateLinkedEntityGUIDsMetadataMap = this.getValueLinkedGUIDsMetadata(this.duplicateValues, metadataMap);
        }
    }

    getValueLinkedGUIDsMetadata(value, linkedEntityGUIDsMetadataMap: LinkedGUIDsMetadataMap): LinkedGUIDsMetadataMap {
        let resultMetadata: LinkedGUIDsMetadataMap = {};

        if (value && typeof value == 'string' && this.isGuidValue(value) && linkedEntityGUIDsMetadataMap[value]) {
            resultMetadata[value] = linkedEntityGUIDsMetadataMap[value];
        } else if (value && Array.isArray(value) && value.length > 0) {
            value.forEach(arrayItem => {
                resultMetadata = {...resultMetadata, ...this.getValueLinkedGUIDsMetadata(arrayItem, linkedEntityGUIDsMetadataMap)};
            });
        } else if (value && typeof value === 'object') {
            Object.values(value).forEach(objectValue => {
                resultMetadata = {...resultMetadata, ...this.getValueLinkedGUIDsMetadata(objectValue, linkedEntityGUIDsMetadataMap)};
            });
        }

        return resultMetadata;
    }

    isGuidValue(value: string): boolean {
        const regexGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return regexGuid.test(value);
    }

    updateAttrStatus() {
        this.attrHasConflict = this.isConflictAttr();
        this.attrHasDifferentValues = this.isDifferentAttrValues();
        this.attrConflictResolved = this.isResolvedConflictAttr();
    }

    setDefaultAttrStatus() {
        this.attrHasConflict = false;
        this.attrHasDifferentValues = false;
        this.attrConflictResolved = false;
    }

    getValue(value) {
        if (Array.isArray(value)) {
            return value.map(val => this.getValue(val));
        } else {
            return this.isObject(value) ? this.getObjectValue(value) : value;
        }
    }

    getPrimaryValue() {
        const outputValue = (this.value != '') ? this.value : this.getValue(this.values);
        if (this.isRelationshipProperty()) {
            return this.convertToOutputFormat(this.values);
        } else {
            return this.convertToOutputFormat(outputValue);
        }
    }

    getNewValue() {
        const outputValue = (this.newValue && this.newValue != '') ? this.newValue : this.getValue(this.newValues);

        if (this.isRelationshipProperty()) {
            return this.convertToOutputFormat(this.newValues);
        } else {
            return this.convertToOutputFormat(outputValue);
        }
    }

    getDuplicateValue() {
        const outputValue = (this.duplicateValue != '') ? this.duplicateValue : this.getValue(this.duplicateValues);
        if (this.isRelationshipProperty()) {
            return this.convertToOutputFormat(this.duplicateValues);
        } else {
            return this.convertToOutputFormat(outputValue);
        }
    }

    getObjectValue(object) {
        const objectCopy = {...object};
        if (this.isPhoneNumberProperty()) {
            return this.getPhoneNumber(objectCopy);
        }

        if (this.isPersonsArrayProperty()) {
            return this.getPersonGuid(objectCopy);
        }

        if (this.isUserRolesProperty()) {
            return UserRole.toString(objectCopy);
        }

        Object.keys(objectCopy).forEach(key => {
            if (objectCopy.hasOwnProperty(key) && !objectCopy[key] || key == 'nameType' || key == 'phoneNumberType') {
                delete objectCopy[key];
            } else {
                objectCopy[key] = this.getValue(objectCopy[key]);
            }
        });
        const values = Object.keys(objectCopy).map(key => objectCopy[key]);
        return (values.length === 1) ? values[0] : values;
    }

    getPhotoLink(value) {
        if (this.name === this.PHOTO_PROPERTY_NAME) {
            return ('<a href="' + value + '" target="_blank">View Photo</a>');
        }
    }

    getRelationshipProperty(value) {
        if (Array.isArray(value)) {
            return value.map(item => this.convertRelationshipToString(item));
        } else {
            return this.convertRelationshipToString(value);
        }
    }

    getGradeProperty(value) {
        return new GradeMapPipe().transform(value);
    }

    getPhoneNumber(value) {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.map(item => item.number).filter(item => !!item) : '';
        } else if (this.isObject(value)) {
            return value['number'] ? value['number'] : '';
        } else {
            return value || '';
        }
    }

    getPersonGuid(value) {
        if (value['guid']) {
            const guidObject = value['guid'];

            return guidObject && guidObject.hasOwnProperty('guidValue') ? this.getGuidValue(guidObject) : '';
        } else {
            return [value['givenName'], value['familyName']].join(' ');
        }
    }

    markAsChanged() {
        this.attrConflictResolved = true;
        this.attrHasConflict = false;
        this.attrHasDifferentValues = false;
    }

    convertToOutputFormat(value) {
        switch (true) {
            // case (this.isHTMLProperty()):
            //     return this.getPhotoLink(value);

            case (this.isRelationshipProperty()):
                return this.getRelationshipProperty(value);

            case (this.isGradeProperty()):
                return this.getGradeProperty(value);

            default: return value;
        }
    }

    convertRelationshipToString(value) {
        const result = [];


        Object.keys(value)
            .map(key => ({ key, value: value[key] }))
            .sort((a1, a2) => a1.key.localeCompare(a2.key))
            .filter(item => !!item.value)
            .forEach(item => {
                if (this.isObject(item.value)) {
                    result.push(this.getObjectValue(item.value));
                } else {
                    const suffix = (item.key === 'relationship') ? ' of ' : '';
                    result.push(item.value + suffix);
                }
            });

        return result.join(' ');
    }

    static uniqueValues(array) {
        const uniqueArray = [];
        array.forEach((value) => {
            uniqueArray[JSON.stringify(value)] = value;
        });

        return Object.keys(uniqueArray).map((key) => uniqueArray[key]);
    }

    isValueEqualToDuplicate(value, values) {
        if ((values && values.length > 0) || (this.duplicateValues && this.duplicateValues.length > 0)) {
            if (Array.isArray(values) || Array.isArray(this.duplicateValues)) {
                return this.isMergeObjectsListProperty()
                    ? !this.isArrayHasDifference(values, this.duplicateValues)
                    : this.isEqualArrayProperty(values, this.duplicateValues);
            } else {
                return this.isEqualObjectProperty(values, this.duplicateValues);
            }
        } else {
            return (this.duplicateValue === '' && !this.sisIdAttrValue) || this.isEqualStringProperty(value, this.duplicateValue);
        }
    }

    isEqualArrayProperty(array1, array2) {
        return (array1 && array2 && (array2.length == array1.length) &&
            array2.every((valueProperty, valueItemIndex) => {
                for (const key in valueProperty) {
                    if (key === 'index' || key === 'nameType') {
                        continue;
                    }
                    if (this.isObject(valueProperty) && this.isObject(array1[valueItemIndex]) && this.isEqualObjectProperty(valueProperty, array1[valueItemIndex])) {
                        return true;
                    }
                }
                return this.isEqualStringProperty(JSON.stringify(valueProperty), JSON.stringify(array1[valueItemIndex]));
            })
        );
    }

    isArrayHasDifference(array1, array2) {
        // If some item2 of array2 is in array1
        if (this.name === USER_ROLES_PROPERTY_NAME) {
            return UserRole.isListDifferent(array1, array2);
        } else {
            return (array2 || []).some((item2) =>
                (array1 || []).some((item1) =>
                    this.isEqualStringProperty(JSON.stringify(item1), JSON.stringify(item2))
                )
            );
        }
    }

    isEqualObjectProperty(object1, object2) {
        if (this.isPhoneNumberProperty()) {
            return (object1['rawPhoneNumber'] && object2['rawPhoneNumber']) ? object1['rawPhoneNumber'] == object2['rawPhoneNumber'] : true;
        } else {
            return (object1 && object2 && (object2.length == object1.length) &&
                Object.keys(object1).every((key) => this.isEqualStringProperty(JSON.stringify(object1[key]), JSON.stringify(object2[key])))
            );
        }
    }

    isEqualStringProperty(value: string, duplicateValue: string) {
        if (value && this.isPhoneNumberProperty()) {
            return this.isEqualPhoneNumbers(value, duplicateValue);
        } else if (typeof value !== 'string' || typeof duplicateValue !== 'string') {
            return JSON.stringify(value) === JSON.stringify(duplicateValue);
        } else {
            return value === duplicateValue;
        }
    }

    isEqualPhoneNumbers(phone: string, duplicatePhone: string) {
        phone = phone.replace(/[^0-9]+/g, '');
        duplicatePhone = duplicatePhone.replace(/[^0-9]+/g, '');

        return (phone.substr(phone.length - 6, 6) === duplicatePhone.substr(duplicatePhone.length - 6, 6));
    }

    isConflictAttr() {
        return this.isDifferentAttrValues() && (this.order > 0);
    }

    isDifferentAttrValues() {
        if (this.order < 0) {
            return false;
        }

        if (this.isEmptyNewValueProperty() && !this.sisIdAttrValue) {
            return !this.isValueEqualToDuplicate(this.newValue, this.newValues)
                && !this.isValueEqualToDuplicate(this.value, this.values)
                && !this.isReadOnlyProperty();
        } else if (this.sisIdAttrValue) {
            return !this.isValueEqualToDuplicate(this.value, this.values)
                && !this.isReadOnlyProperty();
        } else {
            return !this.isValueEqualToDuplicate(this.newValue, this.newValues)
                && !this.isReadOnlyProperty();
        }
    }

    isResolvedConflictAttr() {
        if (this.isEmptyNewValueProperty() && !this.sisIdAttrValue || this.sisIdAttrValue) {
            return false;
        } else {
            return this.isValueEqualToDuplicate(this.newValue, this.newValues) &&
                !this.isValueEqualToDuplicate(this.value, this.values) &&
                !this.isReadOnlyProperty() && (this.order >= 0);
        }
    }

    isSisIdAttr() {
        const sisIdRegExp = new RegExp(`^(sisId\[[a-zA-z0-9]+\]|localId)`, 'g');

        return this.name && !!sisIdRegExp.test(this.name);
    }

    isAllowedToMergeSPEntity() {
        return (this.NOT_ALLOWED_TO_MERGE_SP_ENTITY.indexOf(this.name) < 0);
    }

    isObject(obj) {
        const type = typeof obj;
        return type === 'object' && !!obj && !Array.isArray(obj);
    }

    // isHTMLProperty() {
    //     return (this.HTML_PROPERTIES_LIST.indexOf(this.name) >= 0);
    // }

    isHiddenProperty() {
        return (this.HIDDEN_PROPERTIES_LIST.indexOf(this.name) >= 0)
            || this.HIDDEN_PROPERTIES_WILDCARD_LIST.some((hiddenPropertyName: string) =>
                (this.name.indexOf(hiddenPropertyName) === 0)
            );
    }

    isReadOnlyProperty() {
        return (this.READONLY_PROPERTIES_LIST.indexOf(this.name) >= 0);
    }

    isRelationshipProperty() {
        return (this.RELATIONSHIP_PROPERTIES_LIST.indexOf(this.name) >= 0);
    }

    isGradeProperty() {
        return ['grade', 'Grade', 'grades', 'Grades'].indexOf(this.name) > -1;
    }

    isPhoneNumberProperty() {
        return (this.PHONE_NUMBER_PROPERTIES_LIST.findIndex((propertyName: string) => this.name.includes(propertyName)) >= 0);
    }

    isPersonsArrayProperty() {
        return (this.PERSONS_ARRAY_PROPERTIES_LIST.findIndex((propertyName: string) => this.name.includes(propertyName)) >= 0);
    }

    isPrimaryEnrollmentProperty() {
        return (this.ENROLLMENT_PROPERTIES.indexOf(this.name) >= 0);
    }

    isOtherEnrollmentProperty() {
        return (this.OTHER_ENROLLMENT_PROPERTIES_LIST.indexOf(this.name) >= 0);
    }

    isMergeObjectsListProperty() {
        return (this.MERGE_OBJECTS_LIST_PROPERTIES_LIST.indexOf(this.name) >= 0);
    }

    isEmptyValueProperty() {
        if (this.isPhoneNumberProperty()) {
            return !this.getPhoneNumber(this.value || this.values);
        } else {
            return ((this.value == '') && (!this.values || (this.values.length == 0) || this.isArrayWithEmptyObject(this.values)));
        }
    }

    isEmptyNewValueProperty() {
        return (!this.newValue || this.newValue == '') &&
            (!this.newValues || this.newValues.length == 0 || this.isArrayWithEmptyObject(this.newValues));
    }

    isEmptyDuplicateValueProperty() {
        if (this.isPhoneNumberProperty()) {
            return ((this.duplicateValue == '') && this.duplicateValues.every(value => !value['rawPhoneNumber']));
        }
        return ((this.duplicateValue == '') &&
            (!this.duplicateValues || (this.duplicateValues.length == 0) || this.isArrayWithEmptyObject(this.duplicateValues)));
    }

    isArrayWithEmptyObject(array): boolean {
        return Array.isArray(array) && array.length == 1 && this.isObject(array[0]) && !Object.keys(array[0]).length;
    }

    isUserRolesProperty() {
        return this.name === USER_ROLES_PROPERTY_NAME;
    }

    private getGuidValue(propertyValue): string {
        const guid = propertyValue as Guid;
        return guid ? guid.guidValue : null;
    }
}

export class ShortDomainOfUniquenessModel {
    public id: string;
}

export class DuplicatePositionModel {
    public index: number;
    public totalQuantity: number;

    constructor(index?: number, totalQuantity?: number) {
        this.index = index;
        this.totalQuantity = totalQuantity;
    }
}

export class DuplicateSisIdsChangesModel {
    name: string;
    value: string;
    newValue: string;
    duplicateValue: string;
    attrConflictResolved: boolean;
    changesId: string;

    constructor(name: string, value: string, newValue: string, duplicateValue: string, attrConflictResolved: boolean, changesId: string) {
        this.name = name;
        this.value = value;
        this.newValue = newValue;
        this.duplicateValue = duplicateValue;
        this.attrConflictResolved = attrConflictResolved;
        this.changesId = changesId;
    }
}

export const USER_ROSTER_TYPE = 'User';
export const USER_TYPE_PRIORITY_ORDER = {
    'STUDENT': 1,
    'TEACHER': 2,
    'SCHOOL_ADMIN': 3,
    'CONTACT': 4
};
export const USER_ROLES_PROPERTY_NAME = 'userRoles';
export const SCHOOL_PASSPORT_ENTITY_SIS_ID = 'SchoolDay ID';
